import { waitFor } from '@goodboydigital/astro';
import { Entity, SystemInterface } from '@goodboydigital/odie';
import { Signal, SignalConnection } from 'typed-signals';

import { Shared } from '../../App';
import { BillboardEntity, BillboardFactory } from '../entities/BillboardEntity';
import { CoinEntity } from '../entities/CoinEntity';
import { Game } from '../Game';

export class TutorialSystem implements SystemInterface
{
    public game: Game;
    private billboards: BillboardEntity[];
    private coins: CoinEntity[];
    private signalConnection?: SignalConnection;

    constructor(entity: Entity)
    {
        this.game = entity.scene as Game;
        this.billboards = [];
        this.coins = [];
        this.game.onPause.connect(() => this.handleGamePause());
        this.game.onResume.connect(() => this.handleGameResume());
    }

    private handleGamePause(): void
    {
        Shared.controls.view.visible = false;
    }

    private handleGameResume(): void
    {
        Shared.controls.view.visible = true;
    }

    public async setupTutorial(): Promise<void>
    {
        await waitFor(200);
        BillboardFactory.create(this.game, {
            x: this.game.vehicle.character.x,
            y: -150,
        });

        await waitFor(300);
        Shared.controls.showControls();
        this.tutorialStepAccelerate();
    }

    public tutorialStep(name: string): void
    {
        if (name === 'tutorial-accelerate') this.tutorialStepAccelerate();
        if (name === 'tutorial-ccw') this.tutorialRotateCCW();
        if (name === 'tutorial-cw') this.tutorialRotateCW();
        if (name === 'tutorial-reverse') this.tutorialRotateReverse();
    }

    // === STEP 1 - ACCELERATE ================================================

    private tutorialStepAccelerate(): void
    {
        Shared.controls.disableControls();
        Shared.controls.accelerator.enable();
        Shared.controls.accelerator.highlightOn();
        this.revealBillboard('accelerate');
        this.game.hud.goWidget.show(1);
        this.callback(Shared.controls.accelerator.signals.pressed, this.tutorialStepAccelerateResponse);
    }

    private tutorialStepAccelerateResponse(): void
    {
        Shared.controls.accelerator.highlightOff();
        this.game.soundSystem.playSfx('trick-land');
        this.game.vehicle.motor.handBrakeOff();
        this.callback(Shared.controls.accelerator.signals.released, this.tutorialStepAccelerateComplete);
    }

    private tutorialStepAccelerateComplete(): void
    {
        this.game.vehicle.motor.handBrakeOff();
        this.clearCallback();
    }

    // === STEP 2 - ROTATE CCW ================================================

    private tutorialRotateCCW(): void
    {
        Shared.controls.disableControls();
        Shared.controls.rotatorCCW.enable();
        Shared.controls.rotatorCCW.highlightOn();
        this.hideBillboard('accelerate');
        this.revealBillboard('rotate-ccw');
        this.game.vehicle.motor.handBrakeOn();
        this.callback(Shared.controls.rotatorCCW.signals.pressed, this.tutorialRotateCCWResponse);
    }

    private tutorialRotateCCWResponse(): void
    {
        Shared.controls.rotatorCCW.highlightOff();
        this.game.soundSystem.playSfx('trick-land');
        this.game.vehicle.motor.handBrakeOff();
        this.callback(Shared.controls.rotatorCCW.signals.released, this.tutorialRotateCCWComplete);
    }

    private tutorialRotateCCWComplete(): void
    {
        Shared.controls.accelerator.enable();
        this.game.vehicle.motor.handBrakeOff();
        this.game.hud.goWidget.show(1);
        this.clearCallback();
    }

    // === STEP 3 - ROTATE CW =================================================

    private tutorialRotateCW(): void
    {
        Shared.controls.disableControls();
        Shared.controls.rotatorCW.enable();
        Shared.controls.rotatorCW.highlightOn();
        this.hideBillboard('rotate-ccw');
        this.revealBillboard('rotate-cw');
        this.game.vehicle.motor.handBrakeOn();
        this.game.hud.goWidget.close();
        this.callback(Shared.controls.rotatorCW.signals.pressed, this.tutorialRotateCWResponse);
    }

    private tutorialRotateCWResponse(): void
    {
        Shared.controls.rotatorCW.highlightOff();
        this.game.soundSystem.playSfx('trick-land');
        this.game.vehicle.motor.handBrakeOff();
        this.callback(Shared.controls.rotatorCW.signals.released, this.tutorialRotateCWComplete);
    }

    private tutorialRotateCWComplete(): void
    {
        Shared.controls.accelerator.enable();
        Shared.controls.rotatorCCW.enable();
        this.game.vehicle.motor.handBrakeOff();
        this.clearCallback();
        this.game.hud.goWidget.show(1);
    }

    // === STEP 4 - REVERSE ===================================================

    private tutorialRotateReverse(): void
    {
        Shared.controls.disableControls();
        Shared.controls.brake.enable();
        Shared.controls.brake.highlightOn();
        this.hideBillboard('rotate-cw');
        this.revealBillboard('reverse');
        this.game.vehicle.motor.handBrakeOn();
        this.game.hud.goWidget.close();
        this.callback(Shared.controls.brake.signals.pressed, this.tutorialRotateReverseResponse);
    }

    private tutorialRotateReverseResponse(): void
    {
        Shared.controls.brake.highlightOff();
        this.revealCoins();
        this.game.soundSystem.playSfx('trick-land');
        this.game.vehicle.motor.handBrakeOff();
        this.callback(Shared.controls.brake.signals.released, this.tutorialRotateReverseComplete);
    }

    private tutorialRotateReverseComplete(): void
    {
        Shared.controls.enableControls();
        this.game.hud.goWidget.show(1);
        this.clearCallback();
    }

    // === UTILITY ============================================================

    private getBillboardByName(name: string): BillboardEntity
    {
        for (const billboard of this.billboards)
        {
            if (billboard.name === name) return billboard;
        }

        throw new Error(`Billboard entity not found: ${name}`);
    }

    private revealBillboard(name: string)
    {
        this.getBillboardByName(name).reveal();
    }

    private hideBillboard(name: string)
    {
        this.getBillboardByName(name).hide();
    }

    private callback(signal: Signal<() => void>, cb: () => void)
    {
        this.clearCallback();
        this.signalConnection = signal.connect(cb.bind(this));
    }

    private clearCallback(): void
    {
        if (this.signalConnection) this.signalConnection.disconnect();
        this.signalConnection = undefined;
    }

    private revealCoins(): void
    {
        this.coins.sort((a, b) => b.x - a.x);
        for (let i = 0; i < this.coins.length; i++)
        {
            this.coins[i].reveal((i * 0.3) + 0.3);
        }
    }

    public entityAddedToScene(entity: Entity): void
    {
        if (entity instanceof BillboardEntity)
        {
            this.billboards.push(entity);
        }

        if (entity instanceof CoinEntity)
        {
            this.coins.push(entity);
        }
    }

    public entityRemovedFromScene(entity: Entity): void
    {
        if (entity instanceof BillboardEntity)
        {
            if (this.billboards.includes(entity)) this.billboards.splice(this.billboards.indexOf(entity), 1);
        }

        if (entity instanceof CoinEntity)
        {
            if (this.coins.includes(entity)) this.coins.splice(this.coins.indexOf(entity), 1);
        }
    }

    public update(): void
    {
        //
    }
}
